import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CustomInput, Form, FormGroup, Input, Label, UncontrolledCollapse } from 'reactstrap';
import Menu from '../../components/Menu';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BurgerMenu from '../../components/BurgerMenu';
import { ReactComponent as IconEmail } from '../../images/icn-email.svg';
import { DataTable, DateTimeField, Field } from '../../components/DataTable';
import ModalConfirm from '../../components/ModalConfirm';
import { ALL_STEPS } from '../../components/ProcessStep/steps';
import { EMAIL_TONES } from '../../constants/emailScenarios';
import { getScenarioOptions } from '../../components/AIEmailAssistant/scenarios';
import TemplateEditorModal from './TemplateEditorModal';
import './EmailTemplates.scss';

const formatStageLabel = (t, value = '') => {
  if (!value || value === 'any') return t('ANY');
  return t(`DEALS_STATUSES_${value.toUpperCase()}`);
};

const formatScenarioLabel = (assistantT, value = '') => {
  if (!value || value === 'custom') return assistantT('CUSTOM_TEMPLATE');
  return assistantT(`SCENARIOS.${value}`);
};

const formatLanguageLabel = (assistantT, value = '') => assistantT(`LANGUAGES.${value}`) || value;

function EmailTemplatesPresentational({
  templates,
  total,
  query = {},
  onFilter,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  selectedTemplate,
  editorOpen,
  onCloseEditor,
  onSaveTemplate,
  isSaving,
  isLoading,
}) {
  const [t] = useTranslation('EMAIL_TEMPLATES');
  const [assistantT] = useTranslation('AI_EMAIL_ASSISTANT');
  const [commonT] = useTranslation('COMMON');
  const [searchText, setSearchText] = useState(query.search || '');
  const [tagText, setTagText] = useState(query.tag || '');

  const { funnel_stage = '', scenario = '', language = '', tone = '', offset = 0 } = query;

  React.useEffect(() => {
    setSearchText(query.search || '');
    setTagText(query.tag || '');
  }, [query.search, query.tag]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    onFilter({ search: value, offset: 0 });
  };

  const handleTagChange = (value) => {
    setTagText(value);
    onFilter({ tag: value, offset: 0 });
  };

  const ScenarioField = ({ value }) => <>{formatScenarioLabel(assistantT, value)}</>;
  const StageField = ({ value }) => <>{formatStageLabel(commonT, value)}</>;
  const LanguageField = ({ value }) => <>{formatLanguageLabel(assistantT, value)}</>;
  const ToneField = ({ value }) => <>{assistantT(`TONES.${value}`) || value}</>;
  const TagsField = ({ value }) => <>{Array.isArray(value) ? value.join(', ') : (value || '-')}</>;
  const ActionsField = ({ data }) => (
    <div className="template-actions">
      <Button color="secondary" size="sm" onClick={() => onEditTemplate(data)}>
        <i className="fas fa-pen" />
      </Button>
      <ModalConfirm text={t('DELETE_REQUEST')} onConfirm={() => onDeleteTemplate(data.id)}>
        <Button color="danger" size="sm">
          <i className="fas fa-trash" />
        </Button>
      </ModalConfirm>
    </div>
  );

  return (
    <div className="App email-templates-page">
      <Menu />

      <div className="corpo">
        <section>
          <Header
            title={t('TITLE')}
            icon={<IconEmail fill="#FF6B38" />}
          />

          <div className="row m-b-30 align-items-center">
            <div className="col-12 col-lg-6 mb-3 mb-lg-0">
              <Button className="btn-collapse" id="email-template-filters-toggler" style={{ marginBottom: '1rem' }}>
                {commonT('FILTER_BY')} <i className="fas fa-filter"></i>
              </Button>
            </div>
            <div className="col-12 col-lg-6 text-lg-right">
              <Button className="btn-plus" onClick={onCreateTemplate}>
                <i className="fas fa-plus" /> {t('NEW_TEMPLATE')}
              </Button>
            </div>
          </div>

          <UncontrolledCollapse toggler="#email-template-filters-toggler">
            <div className="email-template-filters">
              <Form inline onSubmit={(event) => event.preventDefault()}>
                <FormGroup>
                  <Label>{commonT('FILTER_BY')}</Label>
                  <Input
                    type="text"
                    value={searchText}
                    className="mx-sm-4"
                    onChange={ev => handleSearchChange(ev.target.value)}
                    placeholder={t('SEARCH_PLACEHOLDER')}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{t('FUNNEL_STAGE')}</Label>
                  <CustomInput
                    id="email-template-funnel-stage-filter"
                    type="select"
                    value={funnel_stage}
                    className="mx-sm-4"
                    onChange={ev => onFilter({ funnel_stage: ev.target.value, offset: 0 })}
                  >
                    <option value="">{t('ALL')}</option>
                    <option value="any">{t('ANY')}</option>
                    {ALL_STEPS.map(step => (
                      <option key={step} value={step}>{commonT(`DEALS_STATUSES_${step.toUpperCase()}`)}</option>
                    ))}
                  </CustomInput>
                </FormGroup>
                <FormGroup>
                  <Label>{t('SCENARIO')}</Label>
                  <CustomInput
                    id="email-template-scenario-filter"
                    type="select"
                    value={scenario}
                    className="mx-sm-4"
                    onChange={ev => onFilter({ scenario: ev.target.value, offset: 0 })}
                  >
                    <option value="">{t('ALL')}</option>
                    <option value="custom">{assistantT('CUSTOM_TEMPLATE')}</option>
                    {getScenarioOptions().map(option => (
                      <option key={option} value={option}>{assistantT(`SCENARIOS.${option}`)}</option>
                    ))}
                  </CustomInput>
                </FormGroup>
                <FormGroup>
                  <Label>{t('LANGUAGE')}</Label>
                  <CustomInput
                    id="email-template-language-filter"
                    type="select"
                    value={language}
                    className="mx-sm-4"
                    onChange={ev => onFilter({ language: ev.target.value, offset: 0 })}
                  >
                    <option value="">{t('ALL')}</option>
                    <option value="it">{assistantT('LANGUAGES.it')}</option>
                    <option value="en">{assistantT('LANGUAGES.en')}</option>
                  </CustomInput>
                </FormGroup>
                <FormGroup>
                  <Label>{t('TONE')}</Label>
                  <CustomInput
                    id="email-template-tone-filter"
                    type="select"
                    value={tone}
                    className="mx-sm-4"
                    onChange={ev => onFilter({ tone: ev.target.value, offset: 0 })}
                  >
                    <option value="">{t('ALL')}</option>
                    {EMAIL_TONES.map(option => (
                      <option key={option} value={option}>{assistantT(`TONES.${option}`)}</option>
                    ))}
                  </CustomInput>
                </FormGroup>
                <FormGroup>
                  <Label>{t('TAGS')}</Label>
                  <Input
                    type="text"
                    value={tagText}
                    className="mx-sm-4"
                    onChange={ev => handleTagChange(ev.target.value)}
                    placeholder={t('TAGS_PLACEHOLDER')}
                  />
                </FormGroup>
              </Form>
            </div>
          </UncontrolledCollapse>

          <hr />

          <Card>
            <CardBody>
              <DataTable
                data={templates}
                total={total}
                offset={offset}
                isLoading={isLoading}
                loadingText={commonT('LOADING')}
                emptyText={t('NO_TEMPLATES')}
                onFilter={onFilter}
              >
                <Field title={t('NAME')} source="name" />
                <StageField title={t('FUNNEL_STAGE')} source="funnel_stage" />
                <ScenarioField title={t('SCENARIO')} source="scenario" />
                <LanguageField title={t('LANGUAGE')} source="language" />
                <ToneField title={t('TONE')} source="tone" />
                <TagsField title={t('TAGS')} source="tags" />
                <DateTimeField title={t('UPDATED_AT')} source="updated_at" />
                <ActionsField title={t('ACTIONS')} />
              </DataTable>
            </CardBody>
          </Card>
        </section>
        <Footer />
      </div>

      <BurgerMenu />

      <TemplateEditorModal
        open={editorOpen}
        template={selectedTemplate}
        toggle={onCloseEditor}
        onSave={onSaveTemplate}
        isSaving={isSaving}
      />
    </div>
  );
}

EmailTemplatesPresentational.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.shape({})),
  total: PropTypes.number,
  query: PropTypes.shape({}),
  onFilter: PropTypes.func.isRequired,
  onCreateTemplate: PropTypes.func.isRequired,
  onEditTemplate: PropTypes.func.isRequired,
  onDeleteTemplate: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.shape({}),
  editorOpen: PropTypes.bool,
  onCloseEditor: PropTypes.func.isRequired,
  onSaveTemplate: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  isLoading: PropTypes.bool,
};

EmailTemplatesPresentational.defaultProps = {
  templates: [],
  total: 0,
  query: {},
  selectedTemplate: null,
  editorOpen: false,
  isSaving: false,
  isLoading: false,
};

export default EmailTemplatesPresentational;
